// inactivity-warning-orchestrator.service.ts
import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { UserInactivityService } from '@shared/services/user-inactivity.service';
import {
  InactivityWarningDialogComponent
} from '@shared/components/organisms/inactivity-warning-dialog/inactivity-warning-dialog.component';
import { CommonFunctionsService } from '@shared/services/common-functions.service';

@Injectable({ providedIn: 'root' })
export class InactivityWarningOrchestratorService implements OnDestroy {
  private dialog = inject(MatDialog);
  private inactivity = inject(UserInactivityService);
  private commonFunctionsService = inject(CommonFunctionsService);

  private dialogRef?: MatDialogRef<InactivityWarningDialogComponent>;
  private subs: Subscription[] = [];
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;

    // Opens or closes the dialog when entering or leaving the warning state
    this.subs.push(
      this.inactivity.inactivityWarning$.subscribe((isWarning) => {
        if (isWarning) {
          const ms = this.inactivity.getRemainingTime();
          this.openDialog(ms);
        } else {
          this.closeDialog();
        }
      })
    );

    // Updates the countdown inside the dialog
    this.subs.push(
      this.inactivity.remainingTime$.subscribe((ms) => {
        if (this.dialogRef) {
          this.dialogRef.componentInstance.updateSecondsFromMs(ms);
        }
      })
    );

    // When the session expires: close the dialog and perform logout
    this.subs.push(
      this.inactivity.timeout$.subscribe(async (expired) => {
        if (expired) {
          this.closeDialog();
          await this.commonFunctionsService.handleLogout();
        }
      })
    );
  }

  stop(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
    this.started = false;
    this.closeDialog();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private openDialog(remainingMs: number) {
    if (this.dialogRef) return;

    this.dialogRef = this.dialog.open(InactivityWarningDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'inactivity-dialog',
      data: {
        seconds: Math.max(0, Math.floor(remainingMs / 1000)),
        onContinue: () => this.inactivity.resetTimer(),
        onLogout: async () => await this.commonFunctionsService.handleLogout(),
      },
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
    });
  }

  private closeDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = undefined;
    }
  }
}
